import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs';

import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { Customer } from '@df/session/api/customer';
import { AbstractDfModalInitiatorService } from '@df/ui/modal/abstract-df-modal-initiator.service';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';

import { Meta } from '@df/core/meta/meta';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { NPwaLoyaltyTermsModal } from './pwa-loyalty-terms-modal.component';

@Injectable({
    providedIn: 'root'
})
export class PwaLoyaltyTermsModalInitiatorService extends AbstractDfModalInitiatorService<NPwaLoyaltyTermsModal.IData> {
    private iCmsBlockResource = inject(ICmsBlockResource);
    private pwaIntegrationService = inject(PwaIntegrationService);
    private customer = inject(Customer);
    private router = inject(Router);
    private meta = inject(Meta);

    private isLocationForbidden(forbiddenLocations?: string): boolean {
        if (!forbiddenLocations) {
            return false;
        }

        // If meta is busy, return true to avoid showing the modal
        if (this.meta.busy) {
            return true;
        }

        const currentUrl = this.router.url;

        const forbiddenUrls = forbiddenLocations
            .split(',')
            .map(url => url.trim())
            .filter(url => url.length > 0);

        return forbiddenUrls.some(forbiddenUrl => currentUrl.includes(forbiddenUrl));
    }

    listen(cb: () => void): Promise<void> {
        return new Promise(resolve => {
            if (!this.pwaIntegrationService.isStandalone) {
                resolve();
                return;
            }

            this.iCmsBlockResource
                .retrieve({ bareUrl: NPwaLoyaltyTermsModal.MODAL_ID }, undefined)
                .then(async (block: ICmsBlock<NPwaLoyaltyTermsModal.IBlockData> | void) => {
                    if (!block || block.data?.content?.notOpenLoyaltyTermsModal) {
                        resolve();
                        return;
                    }

                    await this.customer.model.load();
                    const forbiddenLocations = block.data?.content?.forbiddenLocations;
                    let modalShown = false;

                    const shouldShowModal = (): boolean => {
                        const customerModel = this.customer.model as LocalCustomerModel;
                        return (
                            customerModel.isLoggedIn &&
                            !customerModel.isLoyaltyTermsAccepted &&
                            !this.isLocationForbidden(forbiddenLocations)
                        );
                    };

                    // Check on initial navigation and subsequent navigations
                    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
                        if (shouldShowModal() && !modalShown) {
                            modalShown = true;
                            cb();
                        }
                    });

                    // Also check when customer data changes
                    this.customer.model.obs.data.subscribe(customerModel => {
                        if (
                            customerModel.isLoggedIn &&
                            !(customerModel as LocalCustomerModel).isLoyaltyTermsAccepted &&
                            !this.isLocationForbidden(forbiddenLocations) &&
                            !modalShown
                        ) {
                            modalShown = true;
                            cb();
                        } else if ((customerModel as LocalCustomerModel).isLoyaltyTermsAccepted) {
                            resolve();
                        }
                    });

                    // Check immediately if router is already initialized
                    if (shouldShowModal()) {
                        modalShown = true;
                        cb();
                    }
                })
                .catch(() => resolve());
        });
    }
}
